/* eslint-disable jsx-a11y/label-has-for */
/**
 * @render react
 * @name SimpleSearch
*/

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'recompose';

import { withPolaris } from 'Components/protons/Polaris';

import { Button } from 'Components/atoms/Button';
import { Input } from 'Components/atoms/Input';
import { Form } from 'Components/molecules/Form';
import { Label } from 'Components/molecules/Label';

/**
 * SimpleSearch component
 *
 * @param {string} props.label - label to be used for screen readers
 * @param {string} props.placeholder - text to be displayed on the input
 * @param {string} props.url - url for redirect. With trailing slash
 * @param {string} props.inputSrLabel - accessibility label of the input component for Screen reader
 * @param {Function} props.onSubmit - callback function to be invoked on component submit
 * @param {string} props.submitLabel - Label for the submit button
 * @param {string} props.srLabel - Screen reader label to pass to the button in case the label is not provided
 * @param {Object} props.context - extra information coming from Polaris provider
 * @param {boolean} props.loadStyles - Whether to apply the class names for styling
 * @param {Object} props.extraClassNames - extra classes to be applied to the component
 */
export const SimpleSearch = (props) => {
  const {
    label,
    labelScreenReaderText,
    placeholder,
    url,
    inputId,
    inputName,
    inputSrLabel,
    searchTerm,
    submitLabel,
    srLabel,
    variant,
    closeLabel,
    closeOnClick,
    onSubmit,
    responsive,
    responsiveNavHeading,
    context: { cssPrefix },
    loadStyles,
    extraClassNames,
  } = props;

  const [updatedSearchTerm, setUpdatedSearchTerm] = useState(searchTerm);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    window.location.href = `${url}/${encodeURIComponent(updatedSearchTerm)}`;
    if (onSubmit) {
      if (typeof window !== 'undefined') {
        window.dataLayer.push({
          event: 'searchbar.button_click',
          search_phrase: updatedSearchTerm,
          search_placement: 'navigation bar',
        });
      }
      onSubmit(updatedSearchTerm);
    }
  };

  const handleOnChange = (e) => {
    e.preventDefault();
    const { target: { value } } = e;

    setUpdatedSearchTerm(value);
  };

  return (
    <>
      {responsive && (
        <div className={`${cssPrefix}__search--heading-wrapper`}>
          <p className={`${cssPrefix}__heading -no-spacing`}>{responsiveNavHeading}</p>
          <Button
            type="button"
            label={closeLabel}
            srLabel={closeLabel}
            variant={variant}
            onClick={closeOnClick}
            extraClassNames={{
              [`${cssPrefix}__header--search-button`]: true,
              ...extraClassNames,
            }}
          />
        </div>
      )}
      <div
        className={classNames({
          [`${cssPrefix}__search`]: loadStyles,
          ...extraClassNames,
        })}
      >
        <Form
          action={url}
          onSubmit={handleFormSubmit}
          extraClassNames={{ [`${cssPrefix}__search--form`]: true }}
          role="search"
        >
          <Label
            content={label}
            target={inputId || `${cssPrefix}__search--input`}
            className={`${cssPrefix}__search--label`}
          >
            {labelScreenReaderText && (
              <span className="screen-reader-text">{labelScreenReaderText}</span>
            )}
          </Label>
          <Input
            id={inputId || `${cssPrefix}__search--input`}
            placeholder={placeholder}
            onChange={handleOnChange}
            name={inputName}
            type="search"
            value={updatedSearchTerm}
            srLabel={inputSrLabel}
          />
          <Button
            type="submit"
            label={submitLabel}
            variant={variant}
            srLabel={srLabel}
            disabled={updatedSearchTerm.length === 0}
          />
        </Form>
      </div>
    </>
  );
};

SimpleSearch.propTypes = {
  label: PropTypes.string,
  labelScreenReaderText: PropTypes.string,
  placeholder: PropTypes.string,
  url: PropTypes.string,
  submitLabel: PropTypes.string,
  srLabel: PropTypes.string,
  variant: PropTypes.string,
  closeLabel: PropTypes.string,
  closeOnClick: PropTypes.func,
  searchTerm: PropTypes.string,
  onSubmit: PropTypes.func,
  responsive: PropTypes.bool,
  responsiveNavHeading: PropTypes.string,
  context: PropTypes.shape(),
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.instanceOf(Object),
  inputId: PropTypes.string,
  inputName: PropTypes.string,
  inputSrLabel: PropTypes.string,
};

SimpleSearch.defaultProps = {
  label: 'Search control',
  labelScreenReaderText: '',
  placeholder: 'Search here',
  url: '/search',
  searchTerm: '',
  inputId: null,
  inputName: 'search',
  inputSrLabel: '',
  submitLabel: 'Submit',
  srLabel: 'Search',
  variant: '',
  closeLabel: 'Close',
  closeOnClick: null,
  onSubmit: null,
  responsive: false,
  responsiveNavHeading: 'Search',
  context: {},
  loadStyles: true,
  extraClassNames: {},
};

export default compose(withPolaris)(SimpleSearch);
